/**
 * Conversão de áudio, compartilhada entre o painel do atendente e o WIDGET.
 *
 * O encoder WAV e a decodificação nasceram em
 * `dashboard/components/widgets/WootWriter/utils/mp3ConversionUtils.js`, que continua sendo o
 * dono do MP3 (ele arrasta o `lamejs` junto). O que mora aqui é só o que os DOIS pacotes
 * precisam — e é por isso que este arquivo não importa `lamejs`: o widget é servido dentro de
 * um iframe no celular do morador, e um encoder de MP3 no pacote dele custaria carregamento em
 * toda abertura de conversa para um formato que ninguém pede lá.
 */

const writeString = (view, offset, string) => {
  for (let i = 0; i < string.length; i += 1) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

/**
 * Escreve um AudioBuffer como WAV PCM 16 bits.
 *
 * `numChannels` é lido do chamador, e não do buffer, de propósito: passar `1` num buffer
 * estéreo grava só o canal 0 — é o atalho que a nota de voz usa depois de já ter sido
 * rebaixada para mono pelo `convertToVoiceWav`.
 */
export const bufferToWav = (buffer, numChannels, sampleRate) => {
  const length = buffer.length * numChannels * 2;
  const wav = new ArrayBuffer(44 + length);
  const view = new DataView(wav);

  // Cabeçalho WAV
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + length, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, length, true);

  // Amostras
  const offset = 44;
  const channels = [];
  for (let channel = 0; channel < numChannels; channel += 1) {
    channels.push(buffer.getChannelData(channel));
  }
  for (let i = 0; i < buffer.length; i += 1) {
    for (let channel = 0; channel < numChannels; channel += 1) {
      const sample = Math.max(-1, Math.min(1, channels[channel][i]));
      view.setInt16(
        offset + (i * numChannels + channel) * 2,
        sample * 0x7fff,
        true
      );
    }
  }

  return new Blob([wav], { type: 'audio/wav' });
};

/**
 * O contexto vive o quanto durar a conversão INTEIRA, e não só a decodificação.
 *
 * O iOS limita o número de `AudioContext` vivos por aba, então cada um precisa ser fechado —
 * mas fechar logo depois de `decodeAudioData` deixava o `AudioBuffer` sobreviver ao contexto
 * que o criou, e é exatamente o tipo de coisa em que o WebKit se comporta diferente do
 * Chrome. Aqui o fecho é a última coisa que acontece: quem usa o buffer usa com o contexto
 * dele ainda de pé.
 */
const withAudioContext = async use => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  const audioContext = new AudioContextClass();
  try {
    return await use(audioContext);
  } finally {
    if (typeof audioContext.close === 'function') {
      audioContext.close();
    }
  }
};

const decodeWith = async (audioContext, audioBlob) =>
  audioContext.decodeAudioData(await audioBlob.arrayBuffer());

export const decodeAudioData = async audioBlob =>
  withAudioContext(audioContext => decodeWith(audioContext, audioBlob));

export const convertToWav = async audioBlob =>
  withAudioContext(async audioContext => {
    const audioBuffer = await decodeWith(audioContext, audioBlob);
    return bufferToWav(
      audioBuffer,
      audioBuffer.numberOfChannels,
      audioBuffer.sampleRate
    );
  });

/**
 * 16 kHz mono — a taxa da nota de voz do widget.
 *
 * WAV é PCM cru: o tamanho é `taxa × canais × 2 bytes` por segundo, sem compressão nenhuma.
 * Na taxa em que o navegador entrega (48 kHz, o padrão do `getUserMedia`), um minuto de fala
 * custa ~5,8 MB — o morador está no celular, muitas vezes em rede móvel, e é ele quem paga
 * esse envio. A 16 kHz mono o mesmo minuto custa ~1,9 MB, três vezes menos, e 16 kHz é a
 * banda inteira da voz humana inteligível (o telefone fixo transmite 8 kHz).
 *
 * Ficar em WAV em vez de MP3 é decisão registrada — é o formato que o painel do atendente já
 * grava para os canais que não exigem MP3, desde `c9cae01cb` ("fix: Support audio in safari
 * browser"). A conta acima é o preço disso, e a taxa é o que paga a conta.
 */
export const VOICE_SAMPLE_RATE = 16000;

const renderMonoAt = async (audioBuffer, targetSampleRate) => {
  const OfflineAudioContextClass =
    window.OfflineAudioContext || window.webkitOfflineAudioContext;
  const frames = Math.max(
    1,
    Math.ceil(audioBuffer.duration * targetSampleRate)
  );
  const offline = new OfflineAudioContextClass(1, frames, targetSampleRate);
  const source = offline.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(offline.destination);
  source.start(0);
  return offline.startRendering();
};

/**
 * Converte a gravação do `MediaRecorder` (WebM/Opus no Chrome, MP4/AAC no Safari) para WAV
 * de voz. O reamostrador é o `OfflineAudioContext`, nativo — nenhuma dependência nova.
 *
 * Safari antigo recusava `OfflineAudioContext` fora de 44,1 kHz. Quando isso acontecer o
 * áudio NÃO se perde: cai no WAV na taxa nativa, maior, mas íntegro. Falhar aqui seria
 * descartar a fala que o morador acabou de gravar.
 */
export const convertToVoiceWav = async (
  audioBlob,
  targetSampleRate = VOICE_SAMPLE_RATE
) =>
  withAudioContext(async audioContext => {
    const audioBuffer = await decodeWith(audioContext, audioBlob);
    try {
      const mono = await renderMonoAt(audioBuffer, targetSampleRate);
      // Render vazio é arquivo mudo com cara de arquivo bom: 44 bytes de cabeçalho, player
      // que abre e não toca nada. Se acontecer, cai no WAV da taxa nativa em vez de subir o
      // silêncio.
      if (!mono?.length) throw new Error('empty render');
      return bufferToWav(mono, 1, targetSampleRate);
    } catch (error) {
      return bufferToWav(audioBuffer, 1, audioBuffer.sampleRate);
    }
  });
